import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { toast } from '@/components/ui/use-toast';

// Types for our subscription system
export interface SubscriptionPlan {
  id: string;
  name: string;
  rank: number;
  stripe_product_id: string;
  stripe_price_id?: string; // Add the actual subscribed price ID for current plan
  is_active?: boolean;
  community_id?: string;
  // These fields come from Stripe via get-plan-details
  product_name?: string;
  product_description?: string;
  available_prices?: ProductPrice[];
}

export interface ProductPrice {
  id: string;
  amount: number;
  currency: string;
  interval: string;
  interval_count: number;
  active: boolean;
  discount_percentage?: number | null; // New field for pre-calculated discount
}

export interface ProductPrices {
  product_id: string;
  prices_by_interval: Record<string, ProductPrice[]>;
  available_intervals: string[];
}

export interface SubscriptionStatus {
  subscribed: boolean;
  /**
   * Unified status for the subscription
   * Possible values: 'active', 'inactive', 'error'
   * - 'active': Subscription is currently valid (maps from Stripe statuses: 'active', 'trialing', 'past_due')
   * - 'inactive': Subscription is not active (maps from Stripe statuses: 'canceled', 'unpaid', etc.)
   * - 'error': An error occurred while checking the subscription
   */
  status: string;
  current_plan: SubscriptionPlan | null;
  subscription_end: string | null;
  subscription_id: string | null;
  /** Whether the subscription is set to cancel at the end of the current period */
  cancel_at_period_end: boolean;
  /** The timestamp when the subscription will be canceled */
  cancel_at: string | null;
  /** Last processed webhook event information */
  last_webhook_event?: {
    stripe_event_id: string;
    event_type: string;
    processed_at: string;
  };
}

// New hook to fetch prices for a specific product
export function useProductPrices(productId?: string, communityId?: string) {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['product-prices', productId, communityId],
    queryFn: async () => {
      if (!productId) {
        throw new Error('Product ID is required');
      }

      const { data, error } = await supabase.functions.invoke('get-product-prices', {
        body: { productId, communityId }
      });

      if (error) {
        console.error('Error fetching product prices:', error);
        throw error;
      }

      return data as ProductPrices;
    },
    enabled: !!user && !!productId,
    // Cache for 30 minutes since prices don't change often
    staleTime: 30 * 60 * 1000,
    gcTime: 60 * 60 * 1000
  });
}

export function useSubscriptionPlans(communityId?: string) {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['subscription-plans', communityId],
    queryFn: async () => {
      if (!communityId) {
        console.error('Community ID is required for fetching subscription plans');
        throw new Error('Community ID is required');
      }

      // Get ALL plan information from our database (both active and inactive)
      // Remove the is_active filter to show both active and inactive plans
      const { data: dbPlans, error } = await supabase
        .from('community_subscription_plans')
        .select('*')
        .eq('community_id', communityId)
        .order('rank', { ascending: true });

      if (error) {
        console.error('Error fetching subscription plans:', error);
        throw error;
      }

      // For each plan, fetch detailed information from Stripe using product ID
      const plansWithDetails = await Promise.all(dbPlans.map(async (plan) => {
        try {
          // Call our get-plan-details function to get Stripe product data
          const { data, error } = await supabase.functions.invoke('get-plan-details', {
            body: { productId: plan.stripe_product_id, communityId }
          });
          
          if (error) {
            console.error('Error fetching plan details:', error);
            return {
              ...plan,
              stripe_product_id: plan.stripe_product_id,
              available_prices: []
            }; // Return the basic plan if we can't get details
          }
          
          return data;
        } catch (err) {
          console.error('Error in plan details fetch:', err);
          return {
            ...plan,
            stripe_product_id: plan.stripe_product_id,
            available_prices: []
          }; // Return the basic plan on error
        }
      }));

      return plansWithDetails as SubscriptionPlan[];
    },
    enabled: !!user && !!communityId
  });
}

export function useBusinessSubscription(businessId?: string) {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['business-subscription', businessId],
    queryFn: async () => {
      if (!businessId) {
        throw new Error('Business ID is required');
      }

      const { data, error } = await supabase.functions.invoke('check-subscription', {
        body: { businessId }
      });

      if (error) {
        console.error('Error checking subscription status:', error);
        throw error;
      }

      return data as SubscriptionStatus;
    },
    enabled: !!user && !!businessId,
    // Refresh automatically every minute
    refetchInterval: 60 * 1000
  });
}

// New hook to get webhook event history for debugging
export function useWebhookEventHistory(businessId?: string) {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['webhook-events', businessId],
    queryFn: async () => {
      if (!businessId) {
        throw new Error('Business ID is required');
      }

      const { data, error } = await supabase
        .from('webhook_events')
        .select('id, stripe_event_id, event_type, processing_status, business_id, subscription_id, event_timestamp, processed_at, error_message, retry_count, created_at, updated_at')
        .eq('business_id', businessId)
        .order('event_timestamp', { ascending: false })
        .limit(50);

      if (error) {
        console.error('Error fetching webhook events:', error);
        throw error;
      }

      return data;
    },
    enabled: !!user && !!businessId,
    // Cache for 5 minutes
    staleTime: 5 * 60 * 1000
  });
}

// New hook to get single webhook event with full payload for debugging
export function useWebhookEventDetail(eventId?: string) {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['webhook-event-detail', eventId],
    queryFn: async () => {
      if (!eventId) {
        throw new Error('Event ID is required');
      }

      const { data, error } = await supabase
        .from('webhook_events')
        .select('*') // Include payload for detailed view
        .eq('id', eventId)
        .single();

      if (error) {
        console.error('Error fetching webhook event detail:', error);
        throw error;
      }

      return data;
    },
    enabled: !!user && !!eventId,
    // Cache for 10 minutes since event details don't change
    staleTime: 10 * 60 * 1000
  });
}

export function useSubscriptionCheckout() {
  const { user } = useAuth();
  const [loading, setLoading] = useState<boolean>(false);

  const createCheckoutSession = async (businessId: string, priceId: string) => {
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('create-checkout', {
        body: { businessId, priceId }
      });

      if (error) {
        console.error('Error creating checkout session:', error);
        toast({
          title: 'Error',
          description: 'Failed to create checkout session. Please try again.',
          variant: 'destructive'
        });
        throw error;
      }

      // Redirect to Stripe Checkout
      window.location.href = data.url;
      return data;
    } catch (error) {
      console.error('Error in checkout process:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return {
    createCheckoutSession,
    loading
  };
}

export function useCustomerPortal() {
  const { user } = useAuth();
  const [loading, setLoading] = useState<boolean>(false);

  const redirectToCustomerPortal = async (businessId: string) => {
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('customer-portal', {
        body: { businessId }
      });

      if (error) {
        console.error('Error creating customer portal session:', error);
        toast({
          title: 'Error',
          description: 'Failed to access billing portal. Please try again.',
          variant: 'destructive'
        });
        throw error;
      }

      // Redirect to Stripe Customer Portal
      window.location.href = data.url;
      return data;
    } catch (error) {
      console.error('Error accessing customer portal:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return {
    redirectToCustomerPortal,
    loading
  };
}

// Hook to check if a business has access to a specific feature
export function useFeatureAccess(businessId?: string) {
  const { user } = useAuth();
  const { data: subscription } = useBusinessSubscription(businessId);
  
  const checkFeatureAccess = async (featureKey: string) => {
    if (!businessId || !user) {
      return false;
    }

    // If the subscription is not active, no features are available
    if (!subscription?.subscribed || subscription?.status !== 'active') {
      return false;
    }

    // Call the has_feature RPC to check if the business has access to the feature
    const { data, error } = await supabase.rpc('has_feature', {
      business_id: businessId,
      feature_key: featureKey
    });

    if (error) {
      console.error('Error checking feature access:', error);
      return false;
    }

    return data === true;
  };

  return { checkFeatureAccess };
}
